import { API_URL } from '../lib/api';

/**
 * Appel à l'API du portail depuis son back-office.
 *
 * La SESSION du portail (cookie host-only sur l'API) porte l'autorisation :
 * `AdminGuard` la relit à chaque requête — statut et méthodes
 * d'authentification — si bien qu'une révocation ferme la porte aussitôt.
 *
 * Renvoie la `Response` brute, comme le `apiFetch` des back-offices produits :
 * la file des tickets, reprise de SEmplyApp, s'écrit dans cette forme.
 */
export function adminFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: { ...(options.headers ?? {}) },
  });
}

const MESSAGES_HTTP: Record<number, string> = {
  400: 'Requête invalide.',
  401: 'Session expirée : reconnectez-vous.',
  403: 'Accès refusé.',
  404: 'Introuvable.',
  409: 'Conflit : la donnée a changé entre-temps.',
  413: 'Fichier trop volumineux.',
  422: 'Données invalides.',
  429: 'Trop de requêtes, réessayez dans un instant.',
  500: 'Erreur interne du serveur.',
  502: 'Service indisponible, réessayez plus tard.',
  503: 'Service temporairement indisponible.',
  504: 'Le serveur met trop de temps à répondre.',
};

/**
 * Message lisible d'une réponse en erreur — jamais de JSON ni de HTML brut
 * (même règle que @semply/http dans les produits).
 */
export async function apiErrorMessage(res: Response, fallback?: string): Promise<string> {
  let brut = '';
  try {
    brut = (await res.text()).trim();
  } catch {
    // corps déjà consommé ou illisible : on passe au repli
  }
  if (brut) {
    try {
      const corps = JSON.parse(brut) as { message?: unknown; error?: unknown };
      if (Array.isArray(corps.message) && corps.message.length > 0) return corps.message.join(' • ');
      if (typeof corps.message === 'string' && corps.message.trim()) return corps.message.trim();
      if (typeof corps.error === 'string' && corps.error.trim()) return corps.error.trim();
    } catch {
      if (!brut.startsWith('<') && brut.length <= 200) return brut;
    }
  }
  return fallback || MESSAGES_HTTP[res.status] || `Erreur ${res.status}`;
}

/** Message lisible d'une erreur attrapée (réseau, erreur déjà formatée). */
export function errorMessage(err: unknown, fallback = 'Une erreur est survenue.'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err.trim() || fallback;
  const message = String((err as { message?: unknown }).message ?? '').trim();
  if (!message) return fallback;
  if (/failed to fetch|networkerror|load failed|fetch failed/i.test(message)) {
    return 'Connexion au serveur impossible. Vérifiez votre connexion réseau.';
  }
  return message;
}
